import { FREE_PLAN_HANDLE, FREE_TRIAL_DAYS } from 'constants/billing';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import { useUpgradePlan } from 'hooks/billing/useUpgradePlan';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import FlexBox from '../../../../components/shared/flexbox';
import { SettingUrls } from '../../../../constants/SettingsUrls';
import { WizardStepName } from '../../../../data/ppoints/constants';
import { useAppDispatch } from '../../../../redux/hooks';
import { wizardActions } from '../../../../redux/slices/wizard-slice';
import useWizard from '../../hooks/useWizard';

export const SummaryStep: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const wizard = useWizard();
    const { currentSubscription, plans } = useCurrentSubscriptionHelper();
    const { name: upgradePlanName, trialDays } = useUpgradePlan();

    const handleOpenOwnStep = () => {
        navigate(SettingUrls.billing);
        dispatch(wizardActions.setSelectedStepName(WizardStepName.summary));
    };
    const handleNext = async () => {
        await wizard.markStepDone(WizardStepName.summary);
        navigate(SettingUrls.dashboard);
        // dispatch(wizardActions.setSelectedStepName(WizardStepName.summary));
    };

    const hasActiveSubscription = useMemo(() => !!currentSubscription, [currentSubscription]);
    // Empty until the plans have loaded
    const freePlanName = useMemo(() => plans.find((p) => p.handle === FREE_PLAN_HANDLE)?.name ?? '', [plans]);

    const openOwnStepButton = (
        <div className="inline-flex items-center gap-2">
            <s-button variant="primary" onClick={handleOpenOwnStep}>
                {t('WIZARD.SUMMARY_STEP.BUTTON')}
            </s-button>
            {/* Hidden until the plans have loaded, and for a plan without a trial, so it never says "0-day" */}
            {!hasActiveSubscription && trialDays > 0 && (
                <div>
                    <s-badge tone="info" icon="confetti">
                        {/* <div className="inline-flex items-center justify-center gap-1"> */}
                        {/* <s-icon type="confetti" size="small" /> */}
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t('WIZARD.SUMMARY_STEP.BUTTON_PROMO_TEXT', { trial_days: trialDays }),
                            }}
                        />
                        {/* </div> */}
                    </s-badge>
                </div>
            )}
        </div>
    );
    const nextButton = (
        <s-button variant="primary" onClick={handleNext} inlineSize="fill">
            {t('BUTTON.NEXT')}
        </s-button>
    );

    const buttonToShow: React.ReactNode | null = useMemo(() => {
        if (!location.pathname.startsWith(SettingUrls.billing)) {
            return openOwnStepButton;
        }
        if (hasActiveSubscription) {
            return nextButton; // show next button if ecom has active subscription
        }
        return null;
    }, [hasActiveSubscription, openOwnStepButton, nextButton]);

    return (
        <FlexBox width={'100%'} gap={12} padding={'10px 0 10px 0'}>
            <FlexBox gap={10} flexDirection={'column'}>
                <div
                    dangerouslySetInnerHTML={{
                        __html: t('WIZARD.SUMMARY_STEP.DESCRIPTION', {
                            free_plan_name: freePlanName,
                            plan_name: upgradePlanName,
                            // trialDays is 0 until the plans have loaded (or for a plan without a trial), so it never says "0 days"
                            trial_days: trialDays || FREE_TRIAL_DAYS,
                        }),
                    }}
                />
                <div>{buttonToShow}</div>
            </FlexBox>
        </FlexBox>
    );
};
